
import React, { useState } from 'react';
import { SiteContent, Activity, DynamicStyle } from '../types';
import * as Icons from 'lucide-react';
import { Trash2, Plus, Edit2, Sparkles, Loader2, ArrowUpRight, Upload, ImageOff } from 'lucide-react';
import { optimizeContentAI } from '../services/geminiService';
import { uploadService } from '../services/uploadService';
import { MediaRenderer } from './MediaRenderer';

interface ActivitiesProps {
  data: SiteContent['activities'];
  isAdmin: boolean;
  onUpdate: (data: SiteContent['activities']) => void;
  styles: Record<string, DynamicStyle>;
  onSelect: (id: string) => void;
  activeId: string | null;
}

const Activities: React.FC<ActivitiesProps> = ({ data, isAdmin, onUpdate, styles, onSelect, activeId }) => {
  const [isOptimizing, setIsOptimizing] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const handleUpload = async (id: string) => {
    try {
      setUploadingId(id);
      const base64 = await uploadService.selectAndUpload();
      if (!base64) throw new Error("File conversion returned empty result.");
      
      // Update local state immediately
      editItem(id, { image: base64 });
    } catch (err: any) {
      console.error('Activity Media Upload failed:', err);
      alert(err.message || 'Could not process media. Try a smaller file or a standard format (JPG/PNG/MP4).');
    } finally {
      setUploadingId(null);
    }
  }

  const removeItem = (id: string) => {
    onUpdate({ ...data, items: data.items.filter(i => i.id !== id) });
  };

  const addItem = () => {
    const newItem: Activity = {
      id: Date.now().toString(),
      title: 'New Island Chronicle',
      description: 'Describe a new curated experience for your visitors here.',
      image: '', // Start empty to trigger placeholder
      icon: 'Compass'
    };
    onUpdate({ ...data, items: [...(data.items || []), newItem] });
  };

  const editItem = (id: string, updates: Partial<Activity>) => {
    onUpdate({ 
      ...data, 
      items: data.items.map(i => i.id === id ? { ...i, ...updates } : i) 
    });
  };

  const titleWords = data.title.split(' ');
  const mainTitle = titleWords.slice(0, 1).join(' ');
  const italicTitle = titleWords.slice(1).join(' ');

  return (
    <section id="activities" className="relative py-24 md:py-32 bg-white overflow-hidden scroll-mt-20 px-4 md:px-0">
      <div className="container mx-auto px-6 relative z-10">
        
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-20 md:mb-32 gap-8">
          <div className="max-w-2xl">
            {isAdmin ? (
              <div className="space-y-4 p-8 border-2 border-dashed border-slate-100 rounded-[2.5rem] bg-slate-50/50">
                <input 
                  value={data.subtitle}
                  onChange={e => onUpdate({...data, subtitle: e.target.value})}
                  className="w-full text-sky-500 font-bold tracking-luxury uppercase text-[10px] focus:outline-none bg-transparent"
                />
                <textarea 
                  value={data.title}
                  onChange={e => onUpdate({...data, title: e.target.value})}
                  className="w-full text-3xl md:text-4xl font-bold text-slate-900 focus:outline-none font-serif italic bg-transparent leading-tight resize-none h-24"
                />
              </div>
            ) : (
              <>
                <span className="text-sky-500 font-bold tracking-luxury uppercase text-[10px] mb-8 block">{data.subtitle}</span>
                <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-slate-900 font-serif leading-[1] tracking-tighter">
                  {mainTitle} <span className="italic font-light block mt-3 text-sky-500">{italicTitle}</span>
                </h2>
              </>
            )}
          </div>
          
          {isAdmin && (
            <button 
              onClick={addItem}
              className="flex items-center justify-center gap-4 bg-slate-950 text-white px-10 py-5 rounded-2xl font-bold hover:bg-sky-500 transition-all text-[10px] tracking-luxury uppercase shadow-xl"
            >
              <Plus className="w-5 h-5" /> Create New
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-12">
          {data?.items?.map((activity) => {
            const IconComponent = (Icons as any)[activity.icon] || Icons.HelpCircle;
            const isUploading = uploadingId === activity.id;

            return (
              <div 
                key={activity.id} 
                className="group relative overflow-hidden rounded-[3rem] bg-slate-950 h-[500px] md:h-[650px] transition-all duration-700 hover:-translate-y-2 shadow-sm hover:shadow-2xl"
              >
                {/* Media Layer */}
                <MediaRenderer 
                  src={activity.image} 
                  alt={activity.title} 
                  className={`absolute inset-0 w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 group-hover:scale-110 transition-all duration-1000 ${isUploading ? 'opacity-30' : 'opacity-100'}`}
                />
                
                {/* Explicit Loading Overlay */}
                {isUploading && (
                  <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-slate-950/40 backdrop-blur-sm">
                    <Loader2 className="w-10 h-10 text-white animate-spin mb-4" />
                    <span className="text-[10px] font-bold text-white uppercase tracking-luxury">Syncing Media...</span>
                  </div>
                )}
                
                {/* Shadow/Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/10 to-transparent z-10 pointer-events-none"></div>
                
                {/* Admin Actions */}
                {isAdmin && (
                  <div className="absolute top-6 right-6 flex gap-3 z-40">
                    <button 
                      onClick={() => handleUpload(activity.id)}
                      className="bg-white/90 backdrop-blur-md text-slate-900 p-3.5 rounded-xl shadow-xl hover:bg-sky-500 hover:text-white transition-all disabled:opacity-50"
                      disabled={isUploading}
                      title="Update Media"
                    >
                      <Upload className="w-4 h-4" />
                    </button>
                    <button 
                      onClick={() => removeItem(activity.id)}
                      className="bg-red-500 text-white p-3.5 rounded-xl shadow-xl hover:bg-red-600 transition-all"
                      title="Remove Card"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Content Layer */}
                <div className="absolute bottom-0 left-0 w-full p-10 md:p-14 z-20">
                  {isAdmin ? (
                    <div className="space-y-4 bg-white/10 backdrop-blur-xl p-8 rounded-[2rem] border border-white/20">
                      <div className="flex items-center justify-between mb-2">
                         <input 
                          value={activity.icon}
                          onChange={e => editItem(activity.id, { icon: e.target.value })}
                          className="bg-sky-500/20 text-sky-400 text-[8px] font-bold uppercase px-3 py-1 rounded-full focus:outline-none"
                          placeholder="Icon Name"
                        />
                      </div>
                      <input 
                        value={activity.title}
                        onChange={e => editItem(activity.id, { title: e.target.value })}
                        className="w-full bg-transparent text-2xl font-bold text-white focus:outline-none font-serif italic"
                        placeholder="Activity Title"
                      />
                      <textarea 
                        value={activity.description}
                        onChange={e => editItem(activity.id, { description: e.target.value })}
                        className="w-full bg-transparent text-xs text-white/80 focus:outline-none h-20 resize-none font-light leading-relaxed"
                        placeholder="Description..."
                      />
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-6 mb-8">
                         <div className="p-4 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20">
                            {IconComponent && <IconComponent className="w-6 h-6 text-sky-400" strokeWidth={1.5} />}
                         </div>
                         <div className="h-px flex-1 bg-white/20"></div>
                      </div>
                      <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 font-serif italic tracking-tight group-hover:text-sky-400 transition-colors duration-500">
                        {activity.title}
                      </h3>
                      <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10 line-clamp-3 font-light">
                        {activity.description}
                      </p>
                      <button className="flex items-center gap-5 text-white font-bold text-[10px] tracking-luxury uppercase border-b border-white/20 pb-2 group-hover:border-sky-500 transition-all duration-700">
                        Explore Passage <ArrowUpRight className="w-4 h-4 text-sky-400" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="absolute top-0 right-0 w-[50%] h-full opacity-[0.03] select-none pointer-events-none flex items-center justify-center -z-10">
        <span className="text-[30rem] font-serif font-bold tracking-tighter text-slate-900 leading-none">02</span>
      </div>
    </section>
  );
};

export default Activities;
